import { call, put } from 'redux-saga/effects';
import {
  Types,
  INewLongSteelOrdersResponse,
  IOpenOrderData,
  ITrackingFlatSteel,
  IBilledOrderTracking,
  IBilledOrderData,
  IQCFileResponse,
  IDownloadNFEResponse,
  ITrackingLongSteel,
  ICgCertificateResponse,
  CgCertificateStatus,
  IUpdateItems,
  IUpdateOrderValidationResponse,
  IUpdateOrderValidation,
  IUpdateOrderRequest,
  IUpdateOrderResponse,
  IOrdersChageDataResponse,
  IOrdersChangeData,
  SalesOrderItem,
  Billing
} from './types';
import {
  loadNewLongSteelOrdersSuccess,
  loadNewLongSteelOrdersFailure,
  loadNewTrackingFlatSteelSuccess,
  loadNewTrackingFlatSteelFailure,
  loadNewBilledOrderTrackingSuccess,
  loadNewBilledOrderTrackingFailure,
  loadNewQCFileSuccess,
  loadNewQCFileFailure,
  loadNewNFFileSuccess,
  loadNewNFFileFailure,
  loadNewShippingNoteFileSuccess,
  loadNewShippingNoteFileFailure,
  loadNewTrackingLongSteelSuccess,
  loadNewTrackingLongSteelFailure,
  cgNewQcRequestSuccess,
  cgNewQcRequestFailure,
  loadUpdateOrderItemsSuccess,
  loadUpdateOrderItemsFailure,
  validationUpdateItemsSuccess,
  validationUpdateItemsFailure,
  loadOptionsUpdateItemsSuccess,
  loadOptionsUpdateItemsFailure,
  loadOrderDetailSuccess,
  loadOrderDetailFailure,
  loadXmlSuccess,
  loadXmlFailure
} from './actions';
import { NewLongSteelOrdersService } from '../../../services/NewLongSteelOrders';
import { CustomersService } from '../../../services/Customers';
import { ICustomer } from '../customers/types';
import { loadCustomerFromIdSuccess } from '../customers/actions';
import { ECustomerTypeView } from '../user/types';
import { addToast } from '../toast/actions';
import { EToastTypes } from '../toast/types';
import { history } from '../../../hooks/history';
import { updateQueryParam } from '../../../utils/updateQueryParams';
import { ENewLongSteelOrdersRoutes } from '../../../pages/Orders/NewLongSteelOrders';
import { EOrderTab } from '../long-steel-orders/types';
import { DownloadAreaService } from '../../../services/DownloadArea';
import { IInvoiceXMLResponse } from '../invoices/types';
import { AxiosError } from 'axios';
import { handleCopyAndEmail } from '../../../utils/formatStrings';

interface ILoadOrdersParams {
  payload: {
    states: string[];
    customerIds: string[];
    customerViewType: ECustomerTypeView;
    startDate: string;
    endDate: string;
    needToLoadCustomer?: boolean;
    isManager: boolean;
    orderType?: EOrderTab;
  };
  type: Types;
}

interface ILoadOrderTrackingParams {
  payload: {
    openOrderItem: IOpenOrderData;
    billedOrderItem: IBilledOrderData;
  };
  type: Types;
}

interface IDownloadOrder {
  payload: {
    order?: SalesOrderItem;
    orders?: SalesOrderItem[];
  };
  type: Types;
}

interface IDownloadBilling {
  payload: {
    billings?: Billing[];
  };
  type: Types;
}

interface ILoadOrderLongSteelTrackingParams {
  payload: {
    openOrderItemId: string;
    customerId: string;
    salesOrder: number;
    salesOrderItem: number;
  };
  type: Types;
}

interface ILoadCgQualityCertificateParams {
  type: Types;
  payload: {
    billedOrderItem: IBilledOrderData;
  };
}

interface IUpdateItemsParams {
  payload: {
    updateItems: Partial<IUpdateItems>;
  };
  type: Types;
}

interface ILoadOrderDetailParams {
  payload: {
    orderId: string;
    customerId: string;
    customerType: ECustomerTypeView;
    onSuccess: () => void;
  };
  type: Types;
}

interface ILoadXMLParams {
  payload: {
    billingNumber: string;
  };
  type: Types;
}

interface File {
  cqId: string;
  deliveryItemNumber: string;
  status: 'SUCCESS' | 'FAILED';
}

export const downloadFile = ({
  billings,
  nfe,
  qc,
  shipping
}: {
  billings: Billing[];
  nfe?: IDownloadNFEResponse;
  qc?: IQCFileResponse;
  shipping?: IDownloadNFEResponse;
}) => {
  return billings.forEach((item) => {
    let dataurl;
    let archive;
    if (nfe && nfe?.FILE && item && item.danfeNumber) {
      dataurl = 'data:text/plain;base64,' + nfe.FILE;
      archive = `NF-${item.invoiceNumber || item.danfeNumber}.pdf`;
    }
    if (qc && qc?.qcFile && item && item.cqNumber) {
      dataurl = 'data:text/plain;base64,' + qc.qcFile.qcBase64File;
      archive = `cq-${item.cqNumber}.pdf`;
    }
    if (
      shipping &&
      shipping?.FILE &&
      item &&
      (item?.invoiceZvoi || item?.deliveryDocumentZvoi)
    ) {
      dataurl = 'data:text/plain;base64,' + shipping.FILE;
      archive = `nota-remessa-${
        item.invoiceZvoi || item.deliveryDocumentZvoi
      }.pdf`;
    }

    if (!dataurl || !archive) {
      return;
    }
    const element = document.createElement('a');
    element.setAttribute('href', dataurl);
    element.setAttribute('download', archive);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  });
};

export function* loadNewLongSteelOrdersFromLocalStorageSaga() {
  try {
    const ordersService = new NewLongSteelOrdersService();
    const ordersResponse: INewLongSteelOrdersResponse = yield call(
      ordersService.getOrdersFromCache.bind(ordersService)
    );
    yield put(loadNewLongSteelOrdersSuccess(ordersResponse));
  } catch (err) {
    console.error('loadLongSteelOrdersFromLocalStorageSaga', err);
    yield put(loadNewLongSteelOrdersFailure(err));
  }
}

export function* loadNewLongSteelOrdersSaga(params: ILoadOrdersParams) {
  const {
    states,
    customerIds,
    customerViewType,
    startDate,
    endDate,
    needToLoadCustomer,
    isManager,
    orderType
  } = params.payload;
  if (needToLoadCustomer) {
    try {
      const customerService = new CustomersService();
      const customer: ICustomer = yield customerService.getCustomerById(
        customerIds[0]
      );
      yield put(loadCustomerFromIdSuccess(customer));
    } catch (err) {
      if (
        err &&
        typeof err === 'object' &&
        'message' in err &&
        err.message === 'BP não encontrado'
      ) {
        yield put(loadNewLongSteelOrdersFailure(err, true));
        return;
      }
      yield put(loadNewLongSteelOrdersFailure(err, false));
      yield put(
        addToast({
          message: 'Falha ao carregar os seus pedidos',
          type: EToastTypes.DANGER,
          durationInMS: 5000
        })
      );
      return;
    }
  }

  if (orderType) {
    try {
      const ordersService = new NewLongSteelOrdersService();
      const ordersResponse: INewLongSteelOrdersResponse =
        yield ordersService.getOrdersOpenOrBilled(
          states,
          customerIds,
          customerViewType,
          startDate,
          endDate,
          orderType,
          isManager
        );

      if (
        ordersResponse &&
        ordersResponse.items &&
        ordersResponse.items.length === 0
      ) {
        yield put(
          addToast({
            type: EToastTypes.INFO,
            message: 'Nenhum pedido encontrado.',
            durationInMS: 5000,
            labelActionOne: 'Fechar'
          })
        );
      }

      yield put(
        loadNewLongSteelOrdersSuccess({ ...ordersResponse, orderType })
      );
      return;
    } catch (err) {
      console.error('loadOrdersSaga', err);
      yield put(loadNewLongSteelOrdersFailure(err));
      yield put(
        addToast({
          message: 'Falha ao carregar os seus pedidos.',
          type: EToastTypes.DANGER,
          durationInMS: 5000
        })
      );
    }
  }

  try {
    const ordersService = new NewLongSteelOrdersService();
    const ordersResponse: INewLongSteelOrdersResponse =
      yield ordersService.getOrders(
        states,
        customerIds,
        customerViewType,
        startDate,
        endDate,
        isManager
      );

    if (
      ordersResponse &&
      ordersResponse.items &&
      ordersResponse.items.length === 0
    ) {
      yield put(
        addToast({
          type: EToastTypes.INFO,
          message: 'Nenhum pedido encontrado.',
          durationInMS: 5000,
          labelActionOne: 'Fechar'
        })
      );
    }

    yield put(loadNewLongSteelOrdersSuccess(ordersResponse));
  } catch (err) {
    console.error('loadOrdersSaga', err);
    yield put(loadNewLongSteelOrdersFailure(err));
    yield put(
      addToast({
        message: 'Falha ao carregar os seus pedidos.',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
  }
}

export function* loadNewTrackingFlatSteelSaga(
  params: ILoadOrderTrackingParams
) {
  const { openOrderItem } = params.payload;
  try {
    const ordersService = new NewLongSteelOrdersService();

    const orderTracking: ITrackingFlatSteel = yield call(
      ordersService.getTrackingFlatSteel.bind(ordersService),
      openOrderItem
    );

    yield put(loadNewTrackingFlatSteelSuccess(openOrderItem, orderTracking));
  } catch (err) {
    console.error('loadTrackingFlatSteelSaga', err);
    yield put(loadNewTrackingFlatSteelFailure(openOrderItem, err));
  }
}

export function* loadNewTrackingLongSteelSaga(
  params: ILoadOrderLongSteelTrackingParams
) {
  const { openOrderItemId, customerId, salesOrder, salesOrderItem } =
    params.payload;
  try {
    const ordersService = new NewLongSteelOrdersService();

    const orderTracking: ITrackingLongSteel = yield call(
      ordersService.getTrackingLongSteel.bind(ordersService),
      customerId,
      salesOrder,
      salesOrderItem
    );

    yield put(loadNewTrackingLongSteelSuccess(openOrderItemId, orderTracking));
  } catch (err) {
    console.error('loadTrackingLongSteelSaga', err);
    yield put(loadNewTrackingLongSteelFailure(openOrderItemId, err));
  }
}

export function* loadNewBilledOrderTrackingSaga(
  params: ILoadOrderTrackingParams
) {
  const { billedOrderItem } = params.payload;
  try {
    const ordersService = new NewLongSteelOrdersService();

    const billedOrderTracking: IBilledOrderTracking = yield call(
      ordersService.getBilledOrderTracking.bind(ordersService),
      billedOrderItem
    );

    yield put(
      loadNewBilledOrderTrackingSuccess(billedOrderItem, billedOrderTracking)
    );
  } catch (err) {
    console.error('loadOpenOrderTrackingItemSteelSaga', err);
    yield put(loadNewBilledOrderTrackingFailure(billedOrderItem, err));
  }
}

export function* loadNewQCFileSaga(params: IDownloadOrder) {
  const { orders } = params.payload;
  try {
    if (!orders) {
      throw new Error();
    }

    const ordersService = new NewLongSteelOrdersService();

    const billing = orders[0].billing ?? orders;

    if (orders.length === 1 && billing.length === 1) {
      const qcFileResponse: IQCFileResponse = yield call(
        ordersService.loadQCFile.bind(ordersService),
        billing
      );

      downloadFile({ billings: billing, qc: qcFileResponse });

      yield put(loadNewQCFileSuccess(orders, qcFileResponse));
      return;
    }

    const qcFileResponse: IQCFileResponse = yield call(
      ordersService.downloadMultipleCQs.bind(ordersService),
      orders
    );

    const filesResponseSuccess = qcFileResponse.qcFile.files.filter(
      (file: File) => file.status === 'SUCCESS'
    );
    if (filesResponseSuccess.length > 0) {
      const dataurl =
        'data:text/plain;base64,' + qcFileResponse.qcFile.qcBase64File;
      const element = document.createElement('a');

      element.setAttribute('href', dataurl);
      element.setAttribute('download', `certificados-de-qualidade.zip`);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    }

    const filesResponseError = qcFileResponse.qcFile.files.filter(
      (file: File) => file.status === 'FAILED'
    );
    if (filesResponseError.length > 0) {
      yield put(
        addToast({
          type: EToastTypes.DANGER,
          message: `Não foi possível realizar o download de todos os certificados.`,
          durationInMS: 5000
        })
      );
    }

    // Método abaixo é download por pdf
    // const qcFileResponse: IQCFileResponse = yield call(
    //   ordersService.loadQCFile.bind(ordersService),
    //   orders.reduce((acc: Billing[], item) => {
    //     if (item.billing) {
    //       return [...acc, ...item.billing];
    //     }

    //     return acc;
    //   }, [])
    // );

    // yield put(loadNewQCFileSuccess(orders, qcFileResponse));
  } catch (err) {
    console.error('loadOpenOrderTrackingItemSteelSaga', err);
    yield put(
      addToast({
        message: 'Falha ao baixar documentos.',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
    yield put(loadNewQCFileFailure(err));
  }
}

export function* loadNewNFFileSaga(params: IDownloadBilling) {
  const { billings } = params.payload;

  try {
    const ordersService = new NewLongSteelOrdersService();

    if (!billings) {
      throw new Error();
    }

    if (billings.length > 1) {
      const downloadNFEResponse: IDownloadNFEResponse = yield call(
        ordersService.loadBulkNFEFile.bind(ordersService),
        billings
      );

      if (!downloadNFEResponse) {
        throw new Error();
      }

      yield put(loadNewNFFileSuccess({ billings }));
      return;
    }

    const downloadNFEResponse: IDownloadNFEResponse = yield call(
      ordersService.loadNFEFile.bind(ordersService),
      billings[0]?.danfeNumber
    );

    downloadFile({ billings, nfe: downloadNFEResponse });

    yield put(loadNewNFFileSuccess({ billings }));
  } catch (err) {
    console.error('loadOpenOrderTrackingItemSteelSaga', err);
    yield put(
      addToast({
        message: 'Falha ao baixar documentos.',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
    yield put(loadNewNFFileFailure(err));
  }
}

export function* loadNewShippingNoteFileSaga(params: IDownloadOrder) {
  const { orders } = params.payload;
  try {
    const ordersService = new NewLongSteelOrdersService();

    const getDocumentRef = (item: SalesOrderItem): string =>
      item?.deliveryDocumentZvoi ??
      item?.billing?.find((b) => b?.deliveryDocumentZvoi)
        ?.deliveryDocumentZvoi ??
      item?.invoiceZvoi ??
      '';

    if (!orders || !orders.some((item) => !!getDocumentRef(item))) {
      throw new Error();
    }

    if (orders.length === 1) {
      const order = orders[0];
      const documentRef = getDocumentRef(order);
      const billingWithDoc = order.billing?.find(
        (b) => b.deliveryDocumentZvoi === documentRef
      );
      const effectiveBillings: Billing[] = billingWithDoc
        ? [billingWithDoc]
        : ([
            {
              invoiceZvoi: order.invoiceZvoi,
              deliveryDocumentZvoi: order.deliveryDocumentZvoi
            }
          ] as Billing[]);

      if (order.billing?.length <= 1 || !billingWithDoc) {
        const downloadNFEResponse: IDownloadNFEResponse = yield call(
          ordersService.loadShippingNoteFile.bind(ordersService),
          documentRef
        );

        downloadFile({
          billings: effectiveBillings,
          shipping: downloadNFEResponse
        });

        yield put(loadNewShippingNoteFileSuccess(orders));
        return;
      }
    }

    yield put(loadNewShippingNoteFileSuccess(orders));
  } catch (err) {
    console.error('loadOpenOrderTrackingItemSteelSaga', err);
    yield put(
      addToast({
        message: 'Falha ao baixar documentos.',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
    yield put(loadNewShippingNoteFileFailure(err));
  }
}

export function* loadNewCgQualityCertificateSaga(
  params: ILoadCgQualityCertificateParams
) {
  const { billedOrderItem } = params.payload;
  try {
    const ordersService = new NewLongSteelOrdersService();
    const response: ICgCertificateResponse = yield call(
      ordersService.solicitateCgCertificate.bind(ordersService),
      billedOrderItem
    );
    yield call(ordersService.cleanOrders.bind(ordersService));
    if (response.ok) {
      yield put(cgNewQcRequestSuccess(billedOrderItem, response));
      if (billedOrderItem.billing.statusQC === CgCertificateStatus.Processing) {
        yield put(
          addToast({
            message:
              'Pedido de certificado recebido! Você será notificado por e-mail após o processamento.',
            type: EToastTypes.INFO,
            durationInMS: 5000
          })
        );
      }
    } else {
      throw new Error('Request not ok');
    }
  } catch (err) {
    yield put(
      addToast({
        message: 'Falha ao baixar documentos.',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
    yield put(cgNewQcRequestFailure(params.payload.billedOrderItem));
  }
}

export function* loadUpdateOrder(params: IUpdateItemsParams) {
  const { itemsSelected, itemsToUpdate, cancelMessage, typeAction } =
    params.payload.updateItems;

  try {
    if (!typeAction || !itemsSelected || itemsSelected.length === 0) {
      throw new Error();
    }
    const ordersService = new NewLongSteelOrdersService();

    const itemsAllowed = itemsSelected.filter((item) => !!item.changeAllowed);

    if (itemsAllowed.length === 0) {
      throw new Error();
    }

    if (typeAction === 'cancel') {
      const bodyToUpdate: IUpdateOrderRequest[] = itemsAllowed.map((order) => ({
        customer: order.soldTo.code,
        customerType: ECustomerTypeView.EMITTER,
        salesOrder: order.salesOrder.toString(),
        salesOrderItem: order.salesOrderItem.toString(),
        status: 'cancelado',
        reasonMessage: cancelMessage ? cancelMessage.trim() : ''
      }));

      const response: IUpdateOrderResponse = yield call(
        ordersService.sendOrderUpdateData,
        bodyToUpdate
      );

      if (response && response.errors && response.errors.length > 0) {
        throw new Error();
      }

      yield put(loadUpdateOrderItemsSuccess({ itemsSelected }));
      updateQueryParam('cancel');
      updateQueryParam('cancel', 'finished');
    }

    if (typeAction === 'update') {
      if (!itemsToUpdate || itemsToUpdate.length === 0) {
        throw new Error();
      }

      const bodyToUpdate = itemsToUpdate;

      const response: IUpdateOrderResponse = yield call(
        ordersService.sendOrderUpdateData,
        bodyToUpdate
      );

      if (response && response.errors && response.errors.length > 0) {
        throw new Error();
      }

      yield put(loadUpdateOrderItemsSuccess({ itemsSelected, itemsToUpdate }));
      updateQueryParam('changes');
      updateQueryParam('changes', 'finished');
    }
  } catch (err) {
    yield put(loadUpdateOrderItemsFailure());
    if (typeAction === 'cancel') {
      yield put(
        addToast({
          message: 'Falha ao solicitar cancelamento.',
          type: EToastTypes.DANGER,
          durationInMS: 5000
        })
      );
      updateQueryParam('cancel');
      return;
    }

    if (typeAction === 'update') {
      yield put(
        addToast({
          message: 'Falha ao solicitar alterações.',
          type: EToastTypes.DANGER,
          durationInMS: 5000
        })
      );
      return;
    }

    yield put(
      addToast({
        message: 'Não é possível realizar alteração para esse pedido',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
    return;
  }
}

export function* loadValidationUpdateItems(params: IUpdateItemsParams) {
  const { itemsSelected, typeAction } = params.payload.updateItems;

  try {
    if (!itemsSelected || !typeAction) {
      throw new Error();
    }

    const ordersService = new NewLongSteelOrdersService();

    const bodyToValidate: IUpdateOrderValidation[] = itemsSelected.map(
      (order) => ({
        orderId: order.id,
        customer: order.soldTo.code
      })
    );

    const enabledToUpdate: IUpdateOrderValidationResponse[] = yield call(
      ordersService.validationToUpdate,
      bodyToValidate
    );

    const itemsWithValidation = itemsSelected.map((item) => ({
      ...item,
      changeAllowed:
        enabledToUpdate.find(
          (itm) => itm.orderId === item.id //&& itm.orderId.includes(item.salesOrder)
        )?.changeAllowed ?? false
    }));

    const itemsNotAllowed = itemsWithValidation.filter(
      (item) => !item.changeAllowed
    );

    if (typeAction === 'cancel') {
      yield put(
        validationUpdateItemsSuccess({
          updateItems: {
            itemsSelected: itemsWithValidation,
            itemsAllowed: itemsWithValidation.filter(
              (item) => !!item.changeAllowed
            )
          }
        })
      );

      if (itemsNotAllowed.length === itemsWithValidation.length) {
        updateQueryParam('cancel', 'notallowed');
        updateQueryParam('type', 'withoutitems');
        return;
      }
      if (itemsNotAllowed.length > 0) {
        updateQueryParam('cancel', 'notallowed');
        return;
      }

      updateQueryParam('cancel', 'progress');
      return;
    }

    if (typeAction === 'update') {
      if (itemsNotAllowed.length === itemsWithValidation.length) {
        yield put(
          validationUpdateItemsSuccess({
            updateItems: {
              itemsSelected: itemsWithValidation,
              itemsAllowed: itemsWithValidation.filter(
                (item) => !!item.changeAllowed
              )
            }
          })
        );

        updateQueryParam('update', 'notallowed');
        updateQueryParam('type', 'withoutitems');
        return;
      }

      const itemsToChangeOrderData: IOrdersChangeData[] = itemsWithValidation
        .filter((item) => !!item.changeAllowed)
        .map((item) => ({
          shipTo: item.shipTo.code,
          soldTo: item.soldTo.code,
          details: [{ gerdauSku: item.sku }]
        }));

      const itemsSelectedOptions: IOrdersChageDataResponse[] = yield call(
        ordersService.changeOrderData,
        itemsToChangeOrderData
      );

      const itemsSelectedWithOptions = itemsWithValidation.map((item) => ({
        ...item,
        measureUnits: itemsSelectedOptions.find(
          (itm) => Number(itm.sku) === Number(item.sku)
        )?.measureUnits
      }));

      if (itemsNotAllowed.length > 0) {
        yield put(
          validationUpdateItemsSuccess({
            updateItems: {
              itemsSelected: itemsSelectedWithOptions,
              itemsAllowed: itemsSelectedWithOptions.filter(
                (item) => !!item.changeAllowed && !!item.measureUnits
              )
            }
          })
        );

        updateQueryParam('update', 'notallowed');
        return;
      }

      yield put(
        validationUpdateItemsSuccess({
          updateItems: {
            itemsSelected: itemsSelectedWithOptions,
            itemsAllowed: itemsSelectedWithOptions.filter(
              (item) => !!item.changeAllowed && !!item.measureUnits
            )
          }
        })
      );

      history.push(
        `${history.location.pathname}${ENewLongSteelOrdersRoutes.UPDATE}`
      );
      return;
    }
  } catch (err) {
    yield put(validationUpdateItemsFailure());
    yield put(
      addToast({
        message: 'Não é possível realizar alteração para esse pedido',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
    return;
  }
}

export function* loadOptionsUpdateItems(params: IUpdateItemsParams) {
  const { itemsSelected } = params.payload.updateItems;

  try {
    if (!itemsSelected || itemsSelected.length === 0) {
      throw new Error();
    }

    const ordersService = new NewLongSteelOrdersService();

    const bodyToValidate: IUpdateOrderValidation[] = itemsSelected.map(
      (order) => ({
        orderId: order.id,
        customer: order.soldTo.code
      })
    );

    const enabledToUpdate: IUpdateOrderValidationResponse[] = yield call(
      ordersService.validationToUpdate,
      bodyToValidate
    );

    const itemsWithValidation = itemsSelected.map((item) => ({
      ...item,
      changeAllowed:
        enabledToUpdate.find(
          (itm) => itm.orderId === item.id //&& itm.orderId.includes(item.salesOrder)
        )?.changeAllowed ?? false
    }));

    yield put(
      loadOptionsUpdateItemsSuccess({
        itemsSelected: itemsWithValidation
      })
    );
  } catch (err) {
    yield put(loadOptionsUpdateItemsFailure());
    yield put(
      addToast({
        message: 'Não é possível realizar alteração para esse pedido',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
    return;
  }
}

export function* loadNewOrderDetail(params: ILoadOrderDetailParams) {
  const { orderId, customerId, customerType, onSuccess } = params.payload;

  try {
    if (!orderId) {
      throw new Error();
    }

    const ordersService = new NewLongSteelOrdersService();

    const orderDetail: SalesOrderItem[] = yield call(
      ordersService.getNewOrderDetail,
      {
        customerId,
        customerType,
        orderId
      }
    );

    if (!orderDetail || !orderDetail || orderDetail.length === 0) {
      throw new Error();
    }

    yield put(loadOrderDetailSuccess(orderDetail));
    onSuccess();
  } catch (err) {
    yield put(loadOrderDetailFailure());
    yield put(
      addToast({
        message: 'Não é possível acessar os dados desse pedido',
        type: EToastTypes.DANGER,
        durationInMS: 5000
      })
    );
    console.error({ NewOrderDetail: err });
    return;
  }
}

export function* loadNewXMLSaga(params: ILoadXMLParams) {
  const { billingNumber } = params.payload;

  try {
    const ordersService = new DownloadAreaService();

    const downloadXmlResponse: IInvoiceXMLResponse = yield call(
      ordersService.loadNFXMLDownloadFile.bind(ordersService),
      billingNumber
    );

    const byteCharacters = atob(downloadXmlResponse.FILE);
    const byteNumbers = byteCharacters
      .split('')
      .map((_, idx) => byteCharacters.charCodeAt(idx));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/xml' });

    const link = document.createElement('a');
    link.download = billingNumber;
    link.href = window.URL.createObjectURL(blob);
    link.click();

    yield put(loadXmlSuccess(billingNumber, downloadXmlResponse));
  } catch (err) {
    yield put(
      addToast({
        message:
          'Ocorreu um erro inesperado, tente novamente em alguns instantes. Se o problema persistir, entre em contato com o suporte',
        type: EToastTypes.DANGER,
        labelActionOne: 'CONTATAR SUPORTE',
        durationInMS: 5000,
        handleClickActionOne: handleCopyAndEmail
      })
    );
    yield put(loadXmlFailure(billingNumber, err as AxiosError));
  }
}
